// Thin fetch wrapper that:
//   - sets X-Request-Id
//   - attaches the access token from a provided getter
//   - throws on non-2xx with a typed Problem
//   - in dev-bypass mode, transparently refreshes an expired JWT via
//     POST /v1/auth/dev-refresh and retries once
//
// Real auth-token getter is injected by the auth layer (Auth0 or dev-bypass).
import { env } from '../env';

export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
}

export class HttpError extends Error {
  readonly status: number;
  readonly problem: Problem;
  constructor(problem: Problem) {
    super(problem.title);
    this.status = problem.status;
    this.problem = problem;
  }
}

export type TokenGetter = () => Promise<string | null>;

/**
 * Called when the client recovers from a 401 by minting a fresh dev JWT.
 * Callers should update their token store so subsequent requests use the
 * new token instead of paying the refresh cost again.
 */
export type OnTokenRefreshed = (token: string) => void;

export interface ApiClientOptions {
  baseUrl?: string;
  getToken: TokenGetter;
  /**
   * Optional. When provided, a 401 response triggers a one-shot call to
   * `POST /v1/auth/dev-refresh` (dev-mode backend endpoint). If that
   * returns a fresh token, the original request is retried once with the
   * new bearer and `onTokenRefreshed(newToken)` is fired so the store
   * stays in sync. Production deployments leave this undefined.
   */
  onTokenRefreshed?: OnTokenRefreshed;
}

export function createApiClient({
  baseUrl = env.API_URL,
  getToken,
  onTokenRefreshed,
}: ApiClientOptions) {
  let pendingRefresh: Promise<string | null> | null = null;

  /**
   * Hit `/v1/auth/dev-refresh` once; coalesce concurrent refreshes so a
   * burst of 401s only spawns one upstream request. Returns the new token
   * or null if refresh isn't available (production / refresh endpoint
   * 404s).
   */
  async function refreshDevToken(): Promise<string | null> {
    if (!onTokenRefreshed) return null;
    if (pendingRefresh) return pendingRefresh;
    pendingRefresh = (async () => {
      try {
        const res = await fetch(new URL('/v1/auth/dev-refresh', baseUrl).toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) return null;
        const body = (await res.json()) as { token?: unknown };
        if (typeof body.token !== 'string') return null;
        onTokenRefreshed(body.token);
        return body.token;
      } catch {
        return null;
      } finally {
        pendingRefresh = null;
      }
    })();
    return pendingRefresh;
  }

  async function attempt<T>(
    path: string,
    init: RequestInit & { json?: unknown },
    overrideToken: string | null,
  ): Promise<{ ok: true; value: T } | { ok: false; status: number; problem: Problem }> {
    const url = new URL(path, baseUrl).toString();
    const headers = new Headers(init.headers);
    const token = overrideToken ?? (await getToken());
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (init.json !== undefined) {
      headers.set('Content-Type', 'application/json');
      init.body = JSON.stringify(init.json);
    }
    headers.set('X-Request-Id', crypto.randomUUID());

    const res = await fetch(url, { ...init, headers });
    if (res.status === 204) return { ok: true, value: undefined as T };
    const ct = res.headers.get('content-type') ?? '';
    if (!res.ok) {
      const problem: Problem =
        ct.includes('problem+json') || ct.includes('json')
          ? ((await res.json()) as Problem)
          : { type: 'about:blank', title: res.statusText, status: res.status };
      return { ok: false, status: res.status, problem };
    }
    if (ct.includes('json')) return { ok: true, value: (await res.json()) as T };
    return { ok: true, value: undefined as T };
  }

  return {
    async request<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
      let result = await attempt<T>(path, init, null);
      if (!result.ok && result.status === 401) {
        // Try to refresh the dev token and retry once. If refresh isn't
        // wired (prod) or fails, surface the original 401.
        const newToken = await refreshDevToken();
        if (newToken) {
          result = await attempt<T>(path, init, newToken);
        }
      }
      if (!result.ok) throw new HttpError(result.problem);
      return result.value;
    },

    get<T>(path: string) {
      return this.request<T>(path, { method: 'GET' });
    },
    post<T>(path: string, json?: unknown) {
      return this.request<T>(path, { method: 'POST', json });
    },
    put<T>(path: string, json?: unknown) {
      return this.request<T>(path, { method: 'PUT', json });
    },
    delete<T>(path: string) {
      return this.request<T>(path, { method: 'DELETE' });
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
